import { useCallback, useEffect, useRef, useState } from "react";
import type { EpisodeView } from "@supermanus/document-model";

const LEGACY_STORAGE_KEY = "supermanus.editorSettings";
const LEGACY_PREFS_PREFIX = "supermanus.userPrefs.";

function prefsKey(userId: string): string {
  return `minproduktion.userPrefs.${userId}`;
}

function legacyPrefsKey(userId: string): string {
  return `${LEGACY_PREFS_PREFIX}${userId}`;
}

/** How horizontal rules between cue rows are shown (print + working view). */
export type RowRuleMode = "all" | "none" | "custom";

/** How manuscript columns are shown (order, or audio only). */
export type ScriptColumnOrder =
  | "visual-audio"
  | "audio-visual"
  | "audio-only";

export type EditorSettings = {
  /** Ask before deleting an empty cue pair with Backspace/Delete. Default: off. */
  confirmDeleteEmptyPair: boolean;
  /**
   * Double Return (Enter on a trailing empty paragraph) inserts a new
   * manuscript row. The first Return in an empty cell is a line break.
   * Default: on.
   */
  newRowOnDoubleReturn: boolean;
  /** Continuously write RTF to a chosen file (or download fallback). Default: off. */
  autoExportRtf: boolean;
  /** Vertical rule between billedside and lydside on screen/print. Default: on. */
  printColumnRule: boolean;
  /**
   * Horizontal rules between rows: all / none / per-cue (`printBorderBelow`).
   * Switching mode does not clear custom per-cue choices.
   */
  rowRuleMode: RowRuleMode;
  /**
   * Optional: confine the row-rule hover/click target to the gutter between
   * columns. Default: on.
   */
  rowRuleHitInGutter: boolean;
  /** Show hours when editing/displaying scene duration. Default: on. */
  durationShowHours: boolean;
  /** Show frames when editing/displaying scene duration. Default: off. */
  durationShowFrames: boolean;
  /** Frame rate used when frames are enabled. Default: 25. */
  durationFps: number;
  /** Default background for scene heading rows. */
  defaultSceneHeaderBg: string;
  /** Show comment highlights and badges in the script editor. */
  showComments: boolean;
  /**
   * Show setup numbers (X-Y) at the top of each billedside cell.
   * X = scene number, Y = row/setup index within the scene.
   */
  showSetupNumbers: boolean;
  /**
   * Show camera notes (KAM N + optional framing text) on billedside cells.
   */
  showCameraNotes: boolean;
  /** Show position notes in column 2 for each setup. */
  showPosition: boolean;
  /** Manuscript column layout: both sides (order) or audio only. */
  scriptColumnOrder: ScriptColumnOrder;
  /**
   * First mouse click on a manuscript (or notes) cell selects the whole cell
   * so format buttons/shortcuts apply to all text. A second click in the same
   * cell places the caret for typing. Default: off. Shift-click always
   * selects the cell, including when this is off.
   */
  clickSelectsCell: boolean;
  /**
   * Prefix episode titles with "Episode X: " in the UI, print and export.
   * Default: off.
   */
  showEpisodeNumbers: boolean;
  /** User order of episode folder tabs. Missing ids are appended in default order. */
  episodeTabOrder?: EpisodeView[];
  /**
   * Extra zoom on top of PaperFit fit-to-width (0.75–1.5). Default: 1.
   * Sticky chrome does not zoom.
   */
  episodeZoom: number;
  /**
   * Local lock: no editing or deletion in the UI (and App persist guards).
   * Independent of license readonly. Default: off.
   */
  viewOnly: boolean;
};

export const DEFAULT_EDITOR_SETTINGS: EditorSettings = {
  confirmDeleteEmptyPair: false,
  newRowOnDoubleReturn: true,
  autoExportRtf: false,
  printColumnRule: true,
  rowRuleMode: "custom",
  rowRuleHitInGutter: true,
  durationShowHours: true,
  durationShowFrames: false,
  durationFps: 25,
  defaultSceneHeaderBg: "#dce8e2",
  showComments: true,
  showSetupNumbers: false,
  showCameraNotes: false,
  showPosition: false,
  scriptColumnOrder: "visual-audio",
  clickSelectsCell: false,
  showEpisodeNumbers: false,
  episodeZoom: 1,
  viewOnly: false,
};

/** Clamp episode zoom to supported steps (0.75–1.5, step 0.1). */
export function clampEpisodeZoom(value: number): number {
  if (!Number.isFinite(value)) return DEFAULT_EDITOR_SETTINGS.episodeZoom;
  const stepped = Math.round(value * 10) / 10;
  return Math.min(1.5, Math.max(0.75, stepped));
}

function parseRowRuleMode(value: unknown): RowRuleMode {
  if (value === "all" || value === "none" || value === "custom") return value;
  return DEFAULT_EDITOR_SETTINGS.rowRuleMode;
}

function parseColumnOrder(value: unknown): ScriptColumnOrder {
  if (
    value === "audio-visual" ||
    value === "visual-audio" ||
    value === "audio-only"
  ) {
    return value;
  }
  return DEFAULT_EDITOR_SETTINGS.scriptColumnOrder;
}

const EPISODE_TAB_IDS: EpisodeView[] = [
  "timing",
  "outline",
  "script",
  "sources",
  "crew",
  "programtext",
  "status",
];

function parseEpisodeTabOrder(value: unknown): EpisodeView[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const allowed = new Set<string>(EPISODE_TAB_IDS);
  const out: EpisodeView[] = [];
  const seen = new Set<string>();
  for (const item of value) {
    if (typeof item !== "string" || !allowed.has(item) || seen.has(item)) {
      continue;
    }
    seen.add(item);
    out.push(item as EpisodeView);
  }
  return out.length ? out : undefined;
}

/** True when only the audio (lydside) column is shown. */
export function isAudioOnlyColumnOrder(order: ScriptColumnOrder): boolean {
  return order === "audio-only";
}

/** Whether a cue should show a horizontal rule below it under the current mode. */
export function cueShowsRowRule(
  cue: { printBorderBelow?: boolean } | null | undefined,
  mode: RowRuleMode,
): boolean {
  if (!cue) return false;
  if (mode === "all") return true;
  if (mode === "none") return false;
  return Boolean(cue.printBorderBelow);
}

export function parseEditorSettings(raw: unknown): EditorSettings {
  if (!raw || typeof raw !== "object") {
    return { ...DEFAULT_EDITOR_SETTINGS };
  }
  const parsed = raw as Partial<EditorSettings>;
  const fps = Number(parsed.durationFps);
  return {
    confirmDeleteEmptyPair: Boolean(parsed.confirmDeleteEmptyPair),
    newRowOnDoubleReturn:
      parsed.newRowOnDoubleReturn === undefined
        ? true
        : Boolean(parsed.newRowOnDoubleReturn),
    autoExportRtf: Boolean(parsed.autoExportRtf),
    printColumnRule:
      parsed.printColumnRule === undefined
        ? true
        : Boolean(parsed.printColumnRule),
    rowRuleMode: parseRowRuleMode(parsed.rowRuleMode),
    rowRuleHitInGutter:
      parsed.rowRuleHitInGutter === undefined
        ? true
        : Boolean(parsed.rowRuleHitInGutter),
    durationShowHours:
      parsed.durationShowHours === undefined
        ? true
        : Boolean(parsed.durationShowHours),
    durationShowFrames: Boolean(parsed.durationShowFrames),
    durationFps: Number.isFinite(fps) && fps > 0 ? Math.floor(fps) : 25,
    defaultSceneHeaderBg:
      typeof parsed.defaultSceneHeaderBg === "string" &&
      parsed.defaultSceneHeaderBg.trim()
        ? parsed.defaultSceneHeaderBg
        : DEFAULT_EDITOR_SETTINGS.defaultSceneHeaderBg,
    showComments:
      parsed.showComments === undefined ? true : Boolean(parsed.showComments),
    showSetupNumbers: Boolean(parsed.showSetupNumbers),
    showCameraNotes: Boolean(parsed.showCameraNotes),
    showPosition: Boolean(parsed.showPosition),
    scriptColumnOrder: parseColumnOrder(parsed.scriptColumnOrder),
    clickSelectsCell:
      parsed.clickSelectsCell === undefined
        ? false
        : Boolean(parsed.clickSelectsCell),
    showEpisodeNumbers: Boolean(parsed.showEpisodeNumbers),
    episodeTabOrder: parseEpisodeTabOrder(parsed.episodeTabOrder),
    episodeZoom:
      parsed.episodeZoom === undefined
        ? DEFAULT_EDITOR_SETTINGS.episodeZoom
        : clampEpisodeZoom(Number(parsed.episodeZoom)),
    viewOnly: Boolean(parsed.viewOnly),
  };
}

function readJson(key: string): unknown {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw) as unknown;
  } catch {
    return null;
  }
}

function clickSelectsDefaultOffKey(userId: string): string {
  return `minproduktion.clickSelectsCellDefaultOff.${userId}`;
}

function rowRuleHitInGutterDefaultOnKey(userId: string): string {
  return `minproduktion.rowRuleHitInGutterDefaultOn.${userId}`;
}

/**
 * One-shot: turn on «Streg-hover kun mellem kolonner» for existing users
 * (previous default was off; an older migration even forced it off).
 */
function applyRowRuleHitInGutterDefaultOn(
  userId: string,
  settings: EditorSettings,
): EditorSettings {
  try {
    const flag = rowRuleHitInGutterDefaultOnKey(userId);
    if (localStorage.getItem(flag)) return settings;
    localStorage.setItem(flag, "1");
    if (settings.rowRuleHitInGutter) return settings;
    const next = { ...settings, rowRuleHitInGutter: true };
    saveSettingsForUser(userId, next);
    return next;
  } catch {
    return { ...settings, rowRuleHitInGutter: true };
  }
}

function applyClickSelectsDefaultOff(
  userId: string,
  settings: EditorSettings,
): EditorSettings {
  try {
    const flag = clickSelectsDefaultOffKey(userId);
    if (localStorage.getItem(flag)) return settings;
    localStorage.setItem(flag, "1");
    if (!settings.clickSelectsCell) return settings;
    const next = { ...settings, clickSelectsCell: false };
    saveSettingsForUser(userId, next);
    return next;
  } catch {
    return { ...settings, clickSelectsCell: false };
  }
}

/** Load prefs for a user; migrate once from the old machine-wide key. */
export function loadSettingsForUser(userId: string): EditorSettings {
  const keyed = readJson(prefsKey(userId)) ?? readJson(legacyPrefsKey(userId));
  if (keyed) {
    return applyRowRuleHitInGutterDefaultOn(
      userId,
      applyClickSelectsDefaultOff(userId, parseEditorSettings(keyed)),
    );
  }

  const legacy = readJson(LEGACY_STORAGE_KEY);
  if (legacy) {
    const migrated = applyRowRuleHitInGutterDefaultOn(
      userId,
      applyClickSelectsDefaultOff(userId, parseEditorSettings(legacy)),
    );
    saveSettingsForUser(userId, migrated);
    try {
      localStorage.removeItem(LEGACY_STORAGE_KEY);
    } catch {
      /* ignore */
    }
    return migrated;
  }

  return { ...DEFAULT_EDITOR_SETTINGS };
}

export function saveSettingsForUser(
  userId: string,
  settings: EditorSettings,
): void {
  try {
    localStorage.setItem(prefsKey(userId), JSON.stringify(settings));
  } catch {
    /* quota / private mode */
  }
}

/** Fetch prefs from API; null if missing/unavailable (keep local cache). */
export async function fetchRemoteSettings(
  apiBase: string,
): Promise<EditorSettings | null> {
  try {
    const res = await fetch(`${apiBase.replace(/\/$/, "")}/user/preferences`, {
      credentials: "include",
    });
    if (!res.ok) return null;
    const data = (await res.json()) as { settings?: unknown };
    if (!data.settings || typeof data.settings !== "object") return null;
    return parseEditorSettings(data.settings);
  } catch {
    return null;
  }
}

/** Push prefs to API; failures are ignored (localStorage remains source of truth offline). */
export async function pushRemoteSettings(
  apiBase: string,
  settings: EditorSettings,
): Promise<void> {
  try {
    await fetch(`${apiBase.replace(/\/$/, "")}/user/preferences`, {
      method: "PUT",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ settings }),
    });
  } catch {
    /* offline / older server without the route */
  }
}

export type EditorSettingsActions = {
  /** Persist the current prefs for this user (also runs on every change). */
  saveNow: () => void;
  /** Restore factory defaults for this user. */
  resetToDefaults: () => void;
};

export function useEditorSettings(
  userId: string,
  options?: { apiBase?: string | null },
) {
  const apiBase = options?.apiBase ?? null;
  const [settings, setSettingsState] = useState<EditorSettings>(() =>
    loadSettingsForUser(userId),
  );
  const settingsRef = useRef(settings);
  settingsRef.current = settings;
  const pushTimerRef = useRef(0);

  useEffect(() => {
    setSettingsState(loadSettingsForUser(userId));
  }, [userId]);

  useEffect(() => {
    if (!apiBase || !userId) return;
    let cancelled = false;
    void (async () => {
      const remote = await fetchRemoteSettings(apiBase);
      if (cancelled) return;
      if (remote) {
        saveSettingsForUser(userId, remote);
        setSettingsState(remote);
        return;
      }
      // Empty remote: migrate local → server once.
      const local = loadSettingsForUser(userId);
      await pushRemoteSettings(apiBase, local);
    })();
    return () => {
      cancelled = true;
    };
  }, [apiBase, userId]);

  const scheduleRemotePush = useCallback(
    (next: EditorSettings) => {
      if (!apiBase) return;
      window.clearTimeout(pushTimerRef.current);
      pushTimerRef.current = window.setTimeout(() => {
        void pushRemoteSettings(apiBase, next);
      }, 500);
    },
    [apiBase],
  );

  useEffect(() => {
    return () => window.clearTimeout(pushTimerRef.current);
  }, []);

  const setSettings = useCallback(
    (patch: Partial<EditorSettings>) => {
      setSettingsState((prev) => {
        const next = { ...prev, ...patch };
        saveSettingsForUser(userId, next);
        scheduleRemotePush(next);
        return next;
      });
    },
    [scheduleRemotePush, userId],
  );

  const saveNow = useCallback(() => {
    saveSettingsForUser(userId, settingsRef.current);
    if (apiBase) void pushRemoteSettings(apiBase, settingsRef.current);
  }, [apiBase, userId]);

  const resetToDefaults = useCallback(() => {
    const next = { ...DEFAULT_EDITOR_SETTINGS };
    saveSettingsForUser(userId, next);
    setSettingsState(next);
    scheduleRemotePush(next);
  }, [scheduleRemotePush, userId]);

  return [settings, setSettings, { saveNow, resetToDefaults }] as const;
}
